/**
 * GalleryShareDialog — direct-share dialog for gallery items. OneDrive-style:
 * pick recipients by email + access level, list existing shares with revoke
 * + access-change controls. Phase 6 extract from pages/Gallery.jsx.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { X as XIcon } from "lucide-react";
import { toast } from "sonner";

export default function GalleryShareDialog({ item, onClose }) {
    const [shares, setShares] = useState([]);
    const [email, setEmail] = useState("");
    const [access, setAccess] = useState("view");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!item) { setShares([]); return; }
        setEmail(""); setAccess("view");
        api.get(`/gallery/${item.id}/direct-shares`).then((r) => setShares(r.data || [])).catch(() => setShares([]));
    }, [item]);

    if (!item) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setBusy(true);
        try {
            await api.post(`/gallery/${item.id}/direct-share`, { email: email.trim(), access });
            const r = await api.get(`/gallery/${item.id}/direct-shares`);
            setShares(r.data || []);
            setEmail("");
            toast.success(`Shared with ${email.trim()} (${access})`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Share failed");
        } finally { setBusy(false); }
    };

    const changeAccess = async (s, newAccess) => {
        try {
            await api.patch(`/gallery/direct-shares/${s.id}`, { access: newAccess });
            setShares((cur) => cur.map((x) => x.id === s.id ? { ...x, access: newAccess } : x));
            toast.success("Access updated");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to update");
        }
    };

    const revoke = async (s) => {
        try {
            await api.delete(`/gallery/direct-shares/${s.id}`);
            setShares((cur) => cur.filter((x) => x.id !== s.id));
            toast.success("Access revoked");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to revoke");
        }
    };

    return (
        <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg" data-testid="gallery-share-dialog">
                <DialogHeader>
                    <DialogTitle>Share "{item.name}"</DialogTitle>
                    <DialogDescription>
                        Grant view, edit, or manage access to anyone — they can be in any organisation.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div className="text-xs text-muted-foreground">
                        Anyone with the access level you grant can use this asset, even from another organisation.
                    </div>
                    <div className="flex gap-2">
                        <input
                            type="email"
                            placeholder="email@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="flex-1 h-9 px-3 bg-background border border-border rounded-sm text-sm"
                            data-testid="share-input-email"
                        />
                        <select
                            value={access}
                            onChange={(e) => setAccess(e.target.value)}
                            className="h-9 px-2 bg-background border border-border rounded-sm text-xs"
                            data-testid="share-input-access">
                            <option value="view">Can view</option>
                            <option value="edit">Can edit</option>
                            <option value="manage">Can manage</option>
                        </select>
                        <Button type="submit" size="sm" disabled={busy} data-testid="share-submit">
                            {busy ? "…" : "Share"}
                        </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground -mt-1">
                        <b>View</b> = read + clone. <b>Edit</b> = view + modify. <b>Manage</b> = edit + delete + re-share.
                    </p>
                </form>
                <div className="border-t border-border pt-3" data-testid="share-existing-list">
                    <div className="eyebrow text-[10px] mb-2">People with access ({shares.length})</div>
                    {shares.length === 0 ? (
                        <div className="text-xs text-muted-foreground">No one yet — invite someone above.</div>
                    ) : (
                        <ul className="space-y-1.5 max-h-56 overflow-y-auto">
                            {shares.map((s) => (
                                <li key={s.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`share-row-${s.id}`}>
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate">{s.invited_user_name || s.invited_user_email}</div>
                                        <div className="text-[11px] text-muted-foreground truncate">{s.invited_user_email}{!s.invited_user_id && " · pending signup"}</div>
                                    </div>
                                    <select
                                        value={s.access}
                                        onChange={(e) => changeAccess(s, e.target.value)}
                                        className="h-7 px-2 bg-background border border-border rounded-sm text-[11px]"
                                        data-testid={`share-access-${s.id}`}>
                                        <option value="view">View</option>
                                        <option value="edit">Edit</option>
                                        <option value="manage">Manage</option>
                                    </select>
                                    <button onClick={() => revoke(s)} className="text-muted-foreground hover:text-destructive" data-testid={`share-revoke-${s.id}`} title="Revoke">
                                        <XIcon className="h-3.5 w-3.5" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
